/**
 * Copyright (C) 2020 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React, { useMemo, useEffect, useRef, useState, useCallback } from 'react';
import ChartCustomTimelineTooltip from '../FullTailView/charts/ChartCustomComponents/ChartCustomTimelineTooltip';

export const FLIGHT_PATH_ANNOTATION_CUSTOM_DATA = 'flightPathAnnotation';
const TOP_PADDING = 22;
const HIDDEN_POSITION = -9999;

interface FlightPathMarkerOverlayViewProps {
  marker: google.maps.Marker;
  map: google.maps.Map;
  mouseOutHandler: React.MouseEventHandler;
  mouseEnterHandler: React.MouseEventHandler;

  // pass through props to the tooltip
  tooltipStep: number;
  onNext?: () => void;
  onPrevious?: () => void;
}

export default function FlightPathMarkerOverlayView({
  marker,
  map,
  mouseOutHandler,
  mouseEnterHandler,
  tooltipStep,
  onNext,
  onPrevious,
}: FlightPathMarkerOverlayViewProps) {
  const container = useRef<HTMLDivElement | null>(null);
  const [mapPoint, setMapPoint] = useState<google.maps.Point | null>(null);

  const overlayView = useMemo(() => {
    const overlay = new google.maps.OverlayView();

    overlay.setMap(map);
    return overlay;
  }, [map]);

  const setMapPointCallback = useCallback(() => {
    const position = marker.getPosition();

    if (position) {
      setMapPoint(overlayView.getProjection().fromLatLngToContainerPixel(position));
    }
  }, [marker, overlayView]);

  useEffect(() => {
    overlayView.onRemove = () => {
      setMapPoint(null);
    };
    overlayView.onAdd = setMapPointCallback;
    overlayView.draw = setMapPointCallback;
  }, [overlayView, setMapPointCallback]);

  useEffect(() => {
    return () => {
      overlayView.setMap(null);
    };
  }, [overlayView]);

  useEffect(() => {
    // call this method anytime a new marker is provided in the props
    setMapPointCallback();
  }, [setMapPointCallback]);

  const point = marker.get(FLIGHT_PATH_ANNOTATION_CUSTOM_DATA);

  return (
    <div
      style={{
        position: 'absolute',
        left: mapPoint ? Math.abs(mapPoint.x) : HIDDEN_POSITION,
        top: mapPoint ? Math.abs(mapPoint.y) - TOP_PADDING : HIDDEN_POSITION,
      }}
      ref={container}
      onMouseLeave={container && container.current ? mouseOutHandler : undefined}
      onMouseEnter={container && container.current ? mouseEnterHandler : undefined}
    >
      {container && container.current && (
        <ChartCustomTimelineTooltip
          point={point}
          container={container.current}
          date={point.x}
          tooltipStep={tooltipStep ? tooltipStep : 0}
          onNext={onNext}
          onPrevious={onPrevious}
        />
      )}
    </div>
  );
}
